import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  useMyEmailSubscriptions,
  useUpsertMyStocklistSubscription,
} from '@/api/email-subscriptions/email-subscriptions.hooks';
import {
  isoToWeekdayName,
  minutesToTimeString,
  timeStringToMinutes,
  weekdayNameToIso,
} from '@/api/email-subscriptions/email-subscriptions.mappers';

/** The stocklist email is delivered Monday–Friday only. */
export const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
] as const;

type Weekday = (typeof WEEKDAYS)[number];

export const adjustPricelistSchema = z.object({
  isEnabled: z.boolean(),
  deliveryTime: z.string(),
  deliveryDays: z.array(z.enum(WEEKDAYS)),
});

type AdjustPricelistFormData = z.infer<typeof adjustPricelistSchema>;

const DEFAULT_VALUES: AdjustPricelistFormData = {
  isEnabled: false,
  deliveryTime: '08:00',
  deliveryDays: [...WEEKDAYS],
};

/** Keeps only weekday names the dialog can render (Mon–Fri). */
function toWeekdayValues(daysOfWeek: number[] | null): Weekday[] {
  return (daysOfWeek ?? [])
    .map(isoToWeekdayName)
    .filter((day): day is Weekday => WEEKDAYS.includes(day as Weekday));
}

interface UseAdjustPricelistFormProps {
  /** The contact employee whose stocklist subscription is being edited. */
  contactEmployeeId: number | null;
  onSuccess?: (data: AdjustPricelistFormData) => void;
  onError?: (error: unknown) => void;
}

/**
 * Drives the customer's stocklist-email schedule dialog. Loads the existing
 * subscription via the JWT-scoped `/me/email-subscriptions` endpoint, maps the
 * canonical `timeOfDay` minutes + ISO `daysOfWeek` into the dialog's `HH:mm` +
 * weekday-name model, and upserts the change on submit.
 */
export function useAdjustPricelistForm({
  contactEmployeeId,
  onSuccess,
  onError,
}: UseAdjustPricelistFormProps) {
  const { data: subscriptions, isPending: isLoading } =
    useMyEmailSubscriptions();
  const upsert = useUpsertMyStocklistSubscription();

  const form = useForm<AdjustPricelistFormData>({
    resolver: zodResolver(adjustPricelistSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const stocklist = subscriptions?.find(
    (subscription) =>
      subscription.topic === 'stocklist' &&
      (contactEmployeeId == null ||
        subscription.contactEmployeeId === contactEmployeeId),
  );

  // Prefill once, when the existing subscription first arrives, so later
  // edits made in the dialog are not clobbered by a background refetch.
  const prefilled = useRef(false);
  useEffect(() => {
    if (isLoading || prefilled.current) return;
    prefilled.current = true;
    if (stocklist) {
      const days = toWeekdayValues(stocklist.daysOfWeek);
      form.reset({
        isEnabled: stocklist.enabled,
        deliveryTime:
          stocklist.timeOfDay != null
            ? minutesToTimeString(stocklist.timeOfDay)
            : DEFAULT_VALUES.deliveryTime,
        deliveryDays: days.length > 0 ? days : DEFAULT_VALUES.deliveryDays,
      });
    }
  }, [isLoading, stocklist, form]);

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      await upsert.mutateAsync({
        enabled: data.isEnabled,
        daysOfWeek: data.deliveryDays.map(weekdayNameToIso),
        timeOfDay: timeStringToMinutes(data.deliveryTime),
        contactEmployeeId: contactEmployeeId ?? undefined,
      });
      onSuccess?.(data);
    } catch (error) {
      onError?.(error);
    }
  });

  return {
    ...form,
    onSubmit,
    isLoading,
    isSubmitting: upsert.isPending,
  };
}
